import { Feedback } from '@dnd-kit/dom';
import {
  DragDropProvider,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
  PointerSensor,
} from '@dnd-kit/react';
import { isNotDefined } from '@sgme/fp';
import { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GRID_CELL_WIDTH } from '@/config/grid.ts';
import {
  alterLayout,
  compactLayout,
  convertLayoutBackToTiles,
  convertTilesToLayout,
  type ForexGridLayout,
} from '@/store/tiles/compaction';
import type { Tile } from '@/store/tiles/types.ts';
import type { PxCoordinates } from '@/types/grid.ts';
import { applyDeltaPX, gridToPx, pxToGrid } from '@/utils/grid/coordinates.ts';
import { deepEqual } from '@/utils/object.ts';

export type DragManagerProps = PropsWithChildren<{
  tiles: readonly Tile[];
  onDragStart: (params: { tile: Tile }) => void;
  // whenever the mouse moves during a drag, even if the positions of the tiles did not change
  // the grid should reflect the changes to the dragged tile position
  // frequent call, keep faster and use throttling inside if needed
  onDragMove: (params: {
    tileElement: Element | undefined;
    draggedTileCurrentPosition: PxCoordinates;
    skeletonPosition: PxCoordinates;
  }) => void;
  // when tiles are displaced to dragged tile moved to a new position
  // the grid should re-render to reflect the new (compacted) layout
  onLayoutChanged: (params: { tiles: readonly Tile[] }) => void;
  onDragCanceled: (params: { draggedTileElement: Element | undefined; position: PxCoordinates }) => void;
  onDragEnd: (params: { draggedTile: Tile; draggedTileElement: Element | undefined; position: PxCoordinates }) => void;
}>;

export const DragManager = ({
  children,
  tiles,
  onDragStart,
  onDragMove,
  onLayoutChanged,
  onDragEnd,
  onDragCanceled,
}: DragManagerProps) => {
  const [draggedTile, setDraggedTile] = useState<Tile | undefined>();

  const gridLayout = useMemo<ForexGridLayout>(() => convertTilesToLayout(tiles), [tiles]);

  // only read and written from event reactive code
  // this makes perf better than using setState and let react re-render
  const lastComputedLayoutRef = useRef<ForexGridLayout>(gridLayout);

  const resetState = useCallback(() => {
    lastComputedLayoutRef.current = gridLayout;
  }, [gridLayout]);

  useEffect(() => {
    lastComputedLayoutRef.current = gridLayout;
  }, [gridLayout]);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const source = event.operation.source;
      if (isNotDefined(source)) {
        console.error('dragStart but no source !');
        return;
      }
      const tile = tiles.find((tile) => tile.tileId === source.id);

      if (isNotDefined(tile)) {
        console.error('start drag but no tile found !');
        return;
      }

      setDraggedTile(tile);

      onDragStart({ tile });
    },
    [tiles, onDragStart],
  );

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      if (!draggedTile) {
        console.error('dragMove drag not started !');
        return;
      }

      const { draggedTileCurrentPxPosition, skeletonPositionInGrid, compactedLayout } = computePositions(
        event,
        draggedTile,
        gridLayout,
      );

      onDragMove({
        tileElement: event.operation.source?.element,
        draggedTileCurrentPosition: draggedTileCurrentPxPosition,
        skeletonPosition: gridToPx(skeletonPositionInGrid),
      });

      if (!deepEqual(compactedLayout, lastComputedLayoutRef.current.items)) {
        lastComputedLayoutRef.current.items = compactedLayout;

        onLayoutChanged({
          tiles: convertLayoutBackToTiles(lastComputedLayoutRef.current),
        });
      }
    },
    [onDragMove, onLayoutChanged, draggedTile, gridLayout],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!draggedTile) {
        console.error('dragEnd but no drag in progress !');
        return;
      }

      const draggedTileElement = event.operation.source?.element;

      if (event.canceled) {
        onDragCanceled({
          draggedTileElement,
          position: gridToPx({ col: draggedTile.x, row: draggedTile.y }),
        });
        resetState();
        return;
      }

      const { compactedLayout } = computePositions(event, draggedTile, gridLayout);

      // positioning the dragged tile directly at the right place (computed by the compactor)
      // because we won't be notified by redux for that
      const draggedTileInCompactedLayout = compactedLayout.find((item) => item.i === draggedTile.tileId);

      if (!draggedTileInCompactedLayout) {
        console.error('inconsistancy, cannot update dragged tile position after drag end');
      }

      onDragEnd({
        draggedTile,
        draggedTileElement,
        position: gridToPx({ col: draggedTileInCompactedLayout?.x ?? 0, row: draggedTileInCompactedLayout?.y ?? 0 }),
      });

      resetState();
    },
    [resetState, onDragCanceled, onDragEnd, draggedTile, gridLayout],
  );

  return (
    <DragDropProvider
      sensors={[PointerSensor]}
      // dnd-kit's Feedback plugin would unmount the dragged tile's element (overlay or not),
      // resetting the tile's react state; we manage the dragging feedback ourselves instead
      plugins={(plugins) =>
        plugins.map((plugin) => (plugin === Feedback ? Feedback.configure({ feedback: 'none' }) : plugin))
      }
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragMove={handleDragMove}
    >
      {children}
    </DragDropProvider>
  );
};

function computePositions(dndEvent: DragEndEvent | DragMoveEvent, draggedTile: Tile, gridLayout: ForexGridLayout) {
  const position = dndEvent.operation.position;

  const deltaPointer = {
    x: position.current.x - position.initial.x,
    y: position.current.y - position.initial.y,
  };

  const draggedTileCurrentPxPosition = applyDeltaPX(
    gridToPx({
      col: draggedTile.x,
      row: draggedTile.y,
    }),
    deltaPointer,
  );

  const skeletonPositionInGrid = pxToGrid(
    applyDeltaPX(draggedTileCurrentPxPosition, {
      x: GRID_CELL_WIDTH / 2,
      y: 0,
    }),
  );

  // if grid cell height finally ends up to > 1
  // optim : TODO do any of the following IFF the skeleton moved since last time (in grid units)
  // if the skeleton did not move, then the compacted layout won't be different

  return {
    draggedTileCurrentPxPosition,
    skeletonPositionInGrid,
    compactedLayout: compactLayout(
      alterLayout(gridLayout, draggedTile.tileId, { x: skeletonPositionInGrid.col, y: skeletonPositionInGrid.row }),
      draggedTile.tileId,
    ),
  };
}
